import React, {useEffect, useMemo, useState} from 'react';
import {connect, useDispatch} from "react-redux";
import {useNavigate, useParams} from "react-router-dom";
import {Formik} from "formik";
import {boolean, number, object, string} from 'yup';
import {Box} from "@mui/material";
import Grid from "@mui/material/Grid2";
import {Article, Check} from "@mui/icons-material";
import Loader from "./layout/loading/loader";
import PageTitle from "./layout/PageTitle";
import Constants from "../../utils/constants.js";
import {toastHelper} from "../../utils/toast-helper.js";
import {usersActions} from "../../modules/users/users.actions";
import {formsActions} from "../../modules/forms/forms.actions";
import {formsService} from "../../modules/forms/forms.service";
import {prestationsActions} from "../../modules/prestations/prestations.actions.js";
import {useCreateFormActeMutation} from "../../modules/formActes/formActes.api.js";
import FormDataField from "./forms/form/FormDataField";
import ActeLavageCard from "./forms/form/ActeLavageCard";
import FormActesField from "./forms/form/FormActesField";
import MobilePreview from "./forms/form/MobilePreview";
import {Eyebrow, UI} from "./forms/form/FormUiKit.js";
import './UsersNewOrEdit.css'

/**
 * Refonte FormNewOrEdit v2 (handoff Claude Design) : configuration organisée
 * par acte — carte Formulaire, carte Acte 1 · Lavage, cartes actes
 * complémentaires, preview mobile synchronisée au survol.
 *
 * Création : les actes complémentaires sont stagés localement puis créés après
 * le POST du form (l'acte principal LAVAGE est garanti côté serveur).
 */

const formValidationSchema = object({
    libelle: string()
        .required("Le libelle est requis")
        .max(255, 'Le libelle ne doit pas depasser 255 caracteres'),
    typologieLavage: string()
        .required("La typologie de lavage est requise")
        .max(255, 'La typologie ne doit pas depasser 255 caracteres'),
    galleryEnabled: boolean(),
    releveTerrainEnabled: boolean(),
    photoGpsStampEnabled: boolean(),
    commentaireEnabled: boolean(),
    client: string()
        .required("Veuillez affecter le formulaire a un client")
        .max(255, 'Le client ne doit pas depasser 255 caracteres'),
});

function SaveBar({dirtyCount, submitting, onCancel}) {
    return (
        <div style={{
            display: "flex", alignItems: "center", gap: 16, background: "#fff",
            border: `1px solid ${UI.hairline}`, borderRadius: 14, padding: "14px 20px",
            boxShadow: "0 2px 6px rgba(33,48,103,.07), 0 6px 16px rgba(33,48,103,.06)",
        }}>
            <span style={{fontSize: 12.5, color: UI.textMuted, display: "inline-flex", alignItems: "center", gap: 6}}>
                <Check sx={{fontSize: 14, color: UI.green}}/>
                {dirtyCount === 0 ? "Aucune modification" : `${dirtyCount} modification${dirtyCount > 1 ? "s" : ""} non enregistrée${dirtyCount > 1 ? "s" : ""}`}
            </span>
            <span
                onClick={onCancel}
                style={{marginLeft: "auto", fontSize: 14, fontWeight: 600, color: UI.textSoft, padding: "10px 20px", cursor: "pointer"}}
            >Annuler</span>
            <button
                type="submit"
                disabled={submitting}
                style={{
                    background: UI.green, color: "#fff", fontSize: 14, fontWeight: 700,
                    padding: "11px 32px", borderRadius: 999, cursor: "pointer", border: "none",
                    boxShadow: "0 8px 20px rgba(136,188,36,.32)", transition: "background .2s",
                    opacity: submitting ? .6 : 1, fontFamily: "inherit",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = UI.greenHover; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = UI.green; }}
            >Enregistrer le formulaire</button>
        </div>
    );
}

function FormsNewOrEdit({...props}) {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const params = useParams();
    const [mode, setMode] = useState("create");
    const [formId, setFormId] = useState(null);
    const [previewActes, setPreviewActes] = useState(null);
    const [hoveredActe, setHoveredActe] = useState(null);
    const [stagedActes, setStagedActes] = useState([]);   // mode création : [{acte, obligatoire}]
    const [createFormActe] = useCreateFormActeMutation();

    useEffect(() => {
        if (params.hasOwnProperty("id")) {
            setMode("edit");
            setFormId(params.id);
        } else {
            setMode("create");
            setFormId(null);
        }
    }, [params]);

    useEffect(() => {
        if (formId && (props.form?.id !== parseInt(formId)) && !props.loading && !props.error) {
            dispatch(formsActions.fetchForm(formId));
        }
    }, [props, dispatch, formId]);

    useEffect(() => {
        if (!props.clients && !props.loading && !props.error) {
            dispatch(usersActions.fetchClients());
        }
    }, [props, props.clients]);

    useEffect(() => {
        if (!props.typologiesLavage && !props.loading && !props.error) {
            dispatch(prestationsActions.fetchTypologies())
        }
    }, [dispatch, props.typologiesLavage]);

    const handleSubmit = async (values, {setSubmitting}) => {
        if (mode === "edit") {
            dispatch(formsActions.updateForm(values, values.id));
            return;
        }
        // Création : POST du form, puis chaînage des actes stagés (le LAVAGE
        // est ajouté côté serveur par FormProcessor).
        try {
            const created = await formsService.createForm(values);
            const formIri = created?.["@id"];
            if (formIri && stagedActes.length > 0) {
                for (let i = 0; i < stagedActes.length; i++) {
                    const s = stagedActes[i];
                    await createFormActe({
                        formIri,
                        form: formIri,
                        acte: s.acte["@id"],
                        ordre: i + 1,
                        obligatoire: s.obligatoire,
                    }).unwrap();
                }
            }
            toastHelper.displaySuccess("Le formulaire a bien été créé.");
            navigate(Constants.APP_ROUTE_ADMIN_FORMS);
        } catch (e) {
            toastHelper.handleError(e, "Oops, une erreur est survenue lors de la création.");
            setSubmitting(false);
        }
    };

    const initialValues = useMemo(() => ({
        id: props.form?.id ?? null,
        libelle: props.form?.libelle || '',
        typologieLavage: props.form?.typologieLavage?.['@id'] || null,
        galleryEnabled: props.form?.galleryEnabled ?? false,
        releveTerrainEnabled: props.form?.releveTerrainEnabled ?? false,
        photoGpsStampEnabled: props.form?.photoGpsStampEnabled ?? false,
        commentaireEnabled: props.form?.commentaireEnabled ?? false,
        client: props.form?.client['@id'] || null,
        minPhotoAvant: props.form?.minPhotoAvant || 1,
        minPhotoApres: props.form?.minPhotoApres || 1,
        minPhotoMaintenance: props.form?.minPhotoMaintenance || 0,
        minPhotoLavageImpossible: props.form?.minPhotoLavageImpossible || 0,
        typologieLavageFallback: props.form?.typologieLavageFallback?.['@id'] || null,
        strategieLavageImpossible: props.form?.strategieLavageImpossible || Constants.STRATEGIE_LAVAGE_IMPOSSIBLE[0].id,
        // Options retirées du produit (plus d'UI) : valeurs conservées dans le
        // payload — colonnes NOT NULL côté API et encore lues par l'app Android.
        hasQrcode: props.form?.hasQrcode ?? false,
        hasBarcode: props.form?.hasBarcode ?? false,
        hasRFID: props.form?.hasRFID ?? false,
        hasVolume: props.form?.hasVolume ?? false,
        hasWaste: props.form?.hasWaste ?? false,
    }), [props.form]);

    if (props.loading) {
        return <Loader/>;
    }

    return (
        <>
            <PageTitle title={mode === "create" ? "Nouveau Formulaire" : "Modifier le formulaire"} icon={<Article/>}/>
            <p style={{fontSize: 14, color: UI.textSoft, margin: "0 0 20px 4px"}}>
                Le formulaire pilote ce que l'opérateur voit sur le mobile. Chaque carte ci-dessous correspond à un acte de l'intervention.
            </p>
            <Formik
                initialValues={initialValues}
                enableReinitialize={true}
                validationSchema={formValidationSchema}
                onSubmit={handleSubmit}
            >
                {({values, errors, touched, handleChange, handleBlur, handleSubmit, isSubmitting}) => {
                    const selectedTypologie = (props.typologiesLavage || [])
                        .find((t) => t['@id'] === values.typologieLavage) || null;

                    // Compteur de modifications : champs Formik modifiés + actes stagés.
                    const dirtyCount = Object.keys(values)
                        .filter((k) => JSON.stringify(values[k]) !== JSON.stringify(initialValues[k]))
                        .length + (mode === "create" ? stagedActes.length : 0);

                    return (
                        <form onSubmit={handleSubmit}>
                            <Grid container spacing={3} sx={{px: {xs: 1, md: 2}}}>
                                {/* COLONNE CONFIG */}
                                <Grid size={{xs: 12, md: 7}}>
                                    <div style={{display: "flex", flexDirection: "column", gap: 20}}>
                                        <FormDataField
                                            clients={props.clients}
                                            values={values} errors={errors} touched={touched}
                                            handleChange={handleChange} handleBlur={handleBlur}
                                        />

                                        <div style={{display: "flex", alignItems: "baseline", gap: 12, margin: "6px 2px -12px"}}>
                                            <Eyebrow>Parcours d'intervention</Eyebrow>
                                            <span style={{fontSize: 12.5, color: UI.textMuted}}>
                                                L'ordre des cartes = l'ordre des actes sur le mobile
                                            </span>
                                        </div>

                                        <ActeLavageCard
                                            values={values} errors={errors}
                                            typologiesLavage={props.typologiesLavage}
                                            hovered={hoveredActe === 'lavage'}
                                            onHover={setHoveredActe}
                                        />

                                        {mode === "edit" ? (
                                            props.form?.['@id'] && String(props.form?.id) === String(formId) ? (
                                                <FormActesField
                                                    key={props.form['@id']}
                                                    formIri={props.form['@id']}
                                                    onChange={setPreviewActes}
                                                    hoveredActe={hoveredActe}
                                                    onHover={setHoveredActe}
                                                />
                                            ) : null
                                        ) : (
                                            <FormActesField
                                                formIri={null}
                                                onChange={setPreviewActes}
                                                hoveredActe={hoveredActe}
                                                onHover={setHoveredActe}
                                                staged={stagedActes}
                                                onStagedChange={setStagedActes}
                                            />
                                        )}

                                        <SaveBar
                                            dirtyCount={dirtyCount}
                                            submitting={isSubmitting && !props.error}
                                            onCancel={() => navigate(Constants.APP_ROUTE_ADMIN_FORMS)}
                                        />
                                    </div>
                                </Grid>

                                {/* COLONNE APERCU */}
                                <Grid size={{xs: 12, md: 5}}>
                                    <Box sx={{position: {md: 'sticky'}, top: 16}}>
                                        <div style={{display: "flex", alignItems: "center", gap: 8, marginBottom: 10, justifyContent: "center"}}>
                                            <span style={{width: 8, height: 8, borderRadius: "50%", background: "#8bbf3f", boxShadow: "0 0 0 3px #E7F0CE"}}/>
                                            <span style={{fontSize: 12, fontWeight: 700, letterSpacing: ".14em", textTransform: "uppercase", color: "#5C8316"}}>
                                                Aperçu mobile en direct
                                            </span>
                                        </div>
                                        <MobilePreview
                                            values={values}
                                            typologie={selectedTypologie}
                                            actes={previewActes ?? (props.form?.formActes || [])}
                                            hovered={hoveredActe}
                                        />
                                    </Box>
                                </Grid>
                            </Grid>
                        </form>
                    );
                }}
            </Formik>
        </>
    )
}

const mapStateToProps = function (state) {
    return {
        clients: state.users.clients,
        typologiesLavage: state.prestations.typologiesLavage,
        form: state.forms.form,
        loading: state.forms.loading || state.users.loading,
        error: state.forms.error || state.users.error
    }
}

export default connect(mapStateToProps)(FormsNewOrEdit);
